import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { sanitizeMessageHtml } from '../../utils/sanitizeMessageHtml';
import { fetchAuthenticatedMediaBlobUrl, isDirectMediaUrl } from '../../utils/profileMediaUrl';

interface RichTextDisplayProps {
  html?: string | null;
  className?: string;
}

const RichTextDisplay: React.FC<RichTextDisplayProps> = ({ html, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sanitized = useMemo(() => sanitizeMessageHtml(html ?? ''), [html]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !sanitized) {
      return undefined;
    }

    const objectUrls: string[] = [];
    const images = container.querySelectorAll<HTMLImageElement>('img[src^="/api/"]');

    images.forEach((img) => {
      const src = img.getAttribute('src');
      if (!src || isDirectMediaUrl(src)) {
        return;
      }
      void fetchAuthenticatedMediaBlobUrl(src).then((blobUrl) => {
        if (!blobUrl) {
          return;
        }
        if (blobUrl.startsWith('blob:')) {
          objectUrls.push(blobUrl);
        }
        img.src = blobUrl;
      });
    });

    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [sanitized]);

  if (!sanitized) return null;

  return (
    <Box
      ref={containerRef}
      className={className}
      sx={{
        '& img': { maxWidth: '100%', height: 'auto' },
        '& a': { color: 'primary.main' },
        '& p': { margin: '0 0 0.75em' },
      }}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

export default RichTextDisplay;
